// Pure helpers for qdrant-collections: no network, no Deno APIs, so they are unit-testable.

// Vector sizes each Voyage model can produce (docs.voyageai.com/docs/embeddings, 2026-10-30).
// Keep identical to VOYAGE_MODEL_DIMENSIONS in src/types/knowledgeProviders.ts.
export const VOYAGE_MODEL_DIMENSIONS: Record<string, number[]> = {
  "voyage-4-large": [1024, 256, 512, 2048],
  "voyage-4": [1024, 256, 512, 2048],
  "voyage-4-lite": [1024, 256, 512, 2048],
  "voyage-3-large": [1024, 256, 512, 2048],
  "voyage-3.5": [1024, 256, 512, 2048],
  "voyage-3.5-lite": [1024, 256, 512, 2048],
  "voyage-code-3": [1024, 256, 512, 2048],
  "voyage-finance-2": [1024],
  "voyage-law-2": [1024],
};

export interface PayloadField {
  name: string;
  type: string;
  indexed: boolean;
}

export interface CollectionSchema {
  points_count: number;
  vector_size: number | null;
  named_vectors?: Record<string, number>;
  distance: string;
  payload_fields: PayloadField[];
  captured_at: string;
}

// Collection names go into a URL path, so keep them to a safe alphabet.
export function isSafeCollectionName(name: unknown): name is string {
  return typeof name === "string" && /^[A-Za-z0-9_.\-]{1,255}$/.test(name) && !/^\.+$/.test(name);
}

// Param name an LLM tool gets for a payload field: [A-Za-z0-9_] only.
export function sanitizeFieldName(name: string): string {
  return String(name).replace(/[^A-Za-z0-9_]/g, "_");
}

const KNOWN_TYPES = new Set(["keyword", "integer", "float", "bool", "text", "datetime", "geo", "uuid"]);

export function normalizeFieldType(type: unknown): string {
  const t = String(type ?? "").toLowerCase();
  if (t === "boolean") return "bool";
  return KNOWN_TYPES.has(t) ? t : t || "keyword";
}

function inferType(value: unknown): string | null {
  if (Array.isArray(value)) {
    const first = value.find((v) => v !== null && v !== undefined);
    return first === undefined ? null : inferType(first);
  }
  if (typeof value === "boolean") return "bool";
  if (typeof value === "number") return Number.isInteger(value) ? "integer" : "float";
  if (typeof value === "string") return "keyword";
  if (value && typeof value === "object") {
    const o = value as Record<string, unknown>;
    if ("lat" in o && "lon" in o) return "geo";
  }
  return null;
}

// Fields seen in sampled payloads, by first-seen type. Nested objects are skipped.
export function fieldsFromSamples(samples: Array<Record<string, unknown>>): Map<string, string> {
  const seen = new Map<string, string>();
  for (const payload of samples) {
    for (const [name, value] of Object.entries(payload ?? {})) {
      if (seen.has(name)) continue;
      const type = inferType(value);
      if (type) seen.set(name, type);
    }
  }
  return seen;
}

// Qdrant's GET /collections/{name} result -> contract schema. `samples` are scrolled payloads.
export function parseCollectionInfo(
  info: any,
  samples: Array<Record<string, unknown>>,
  now: Date = new Date(),
): CollectionSchema {
  const vectors = info?.config?.params?.vectors;
  let vector_size: number | null = null;
  let distance = "Cosine";
  let named_vectors: Record<string, number> | undefined;

  if (vectors && typeof vectors.size === "number") {
    // Single unnamed vector: { size, distance }
    vector_size = vectors.size;
    distance = vectors.distance ?? distance;
  } else if (vectors && typeof vectors === "object") {
    // Named vectors: { name: { size, distance } }
    named_vectors = {};
    for (const [name, cfg] of Object.entries<any>(vectors)) {
      if (cfg && typeof cfg.size === "number") {
        named_vectors[name] = cfg.size;
        distance = cfg.distance ?? distance;
      }
    }
  }

  const fields = new Map<string, PayloadField>();
  for (const [name, cfg] of Object.entries<any>(info?.payload_schema ?? {})) {
    fields.set(name, { name, type: normalizeFieldType(cfg?.data_type), indexed: true });
  }
  for (const [name, type] of fieldsFromSamples(samples)) {
    if (!fields.has(name)) fields.set(name, { name, type, indexed: false });
  }

  const schema: CollectionSchema = {
    points_count: Number(info?.points_count ?? 0),
    vector_size,
    distance,
    payload_fields: [...fields.values()].sort((a, b) => a.name.localeCompare(b.name)),
    captured_at: now.toISOString(),
  };
  if (named_vectors) schema.named_vectors = named_vectors;
  return schema;
}

// Draft of the LLM-facing tool description from what the collection holds.
export function buildDescription(
  collection: string,
  schema: CollectionSchema,
  samples: Array<Record<string, unknown>> = [],
): string {
  const name = collection.replace(/[_\-]+/g, " ").trim();
  const names = schema.payload_fields.map((f) => f.name);
  const shown = names.slice(0, 12).join(", ");
  const more = names.length > 12 ? `, and ${names.length - 12} more` : "";
  const filters = schema.payload_fields.filter((f) => f.indexed).map((f) => f.name);

  const parts = [
    `Search the "${name}" collection (${schema.points_count} records) by meaning.`,
    names.length ? `Each record has: ${shown}${more}.` : "",
    filters.length ? `You can narrow results with these filters: ${filters.join(", ")}.` : "",
  ];

  const sample = samples[0];
  if (sample) {
    const example = Object.entries(sample)
      .filter(([, v]) => typeof v === "string" || typeof v === "number")
      .slice(0, 3)
      .map(([k, v]) => `${k}: ${String(v).slice(0, 60)}`)
      .join("; ");
    if (example) parts.push(`Example record: ${example}.`);
  }
  return parts.filter(Boolean).join(" ");
}

// Voyage output_dimension to request so the query vector matches the collection, or an error.
export function resolveOutputDimension(
  model: string,
  vectorSize: number | null,
): { ok: true; dimension: number | undefined } | { ok: false; reason: string } {
  const sizes = VOYAGE_MODEL_DIMENSIONS[model];
  if (!sizes) return { ok: false, reason: `Unknown embedding model ${model}` };
  if (vectorSize === null) return { ok: true, dimension: undefined };
  if (!sizes.includes(vectorSize)) {
    return {
      ok: false,
      reason: `${model} cannot produce ${vectorSize}-dimension vectors (supports ${sizes.join(", ")})`,
    };
  }
  // The default size needs no output_dimension; models with one size do not accept it.
  return { ok: true, dimension: sizes.length > 1 && vectorSize !== sizes[0] ? vectorSize : undefined };
}

// Payload sent back to the browser: strings cut to 500 chars, only requested fields, no vectors.
export function trimPayload(
  payload: Record<string, unknown> | null | undefined,
  returnedFields: string[] = [],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(payload ?? {})) {
    if (returnedFields.length && !returnedFields.includes(k)) continue;
    out[k] = typeof v === "string" && v.length > 500 ? v.slice(0, 500) : v;
  }
  return out;
}

// A fully qualified domain name: two or more DNS labels, an alphabetic TLD (so no IP address),
// and not a local-only suffix. `localhost`, `qdrant`, `10.0.0.5` and `db.internal` all fail.
function isFqdn(host: string): boolean {
  const h = host.toLowerCase();
  if (h.length > 253) return false;
  const labels = h.split(".");
  if (labels.length < 2) return false;
  if (!labels.every((l) => /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/.test(l))) return false;
  if (!/^[a-z]{2,}$/.test(labels[labels.length - 1])) return false;
  return !["local", "localhost", "internal", "localdomain", "lan", "home", "corp"].includes(labels[labels.length - 1]);
}

// Base URL from provider_credentials.config, no trailing slash. The host must be an FQDN.
// Qdrant is called over REST, so http and https are accepted as written; a URL with no scheme
// (`host.example.com:6333`) gets https. Any other scheme (grpc://, ftp://), credentials in the
// URL, and a query or fragment are rejected; a path (a reverse proxy prefix) is kept.
export function normalizeBaseUrl(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim().replace(/\/+$/, "");
  const m = trimmed.match(/^(?:(https?):\/\/)?([^\s/:@?#]+)(?::(\d{1,5}))?(\/[^\s?#]*)?$/i);
  if (!m || !isFqdn(m[2])) return null;
  if (m[3] && Number(m[3]) > 65535) return null;
  return `${(m[1] ?? "https").toLowerCase()}://${m[2].toLowerCase()}${m[3] ? `:${m[3]}` : ""}${m[4] ?? ""}`;
}

// Rerank models, same ids as VOYAGE_RERANK_MODELS in src/types/knowledgeProviders.ts.
export const VOYAGE_RERANK_MODEL_IDS = [
  "rerank-2.5",
  "rerank-2.5-lite",
  "rerank-2",
  "rerank-2-lite",
  "rerank-1",
  "rerank-lite-1",
];
export const DEFAULT_RERANK_MODEL = "rerank-2.5";

// Text Voyage reranks for one hit: "key: value" lines for scalar payload fields.
export function buildRerankDocument(payload: Record<string, unknown>, maxChars = 2000): string {
  const lines: string[] = [];
  for (const [k, v] of Object.entries(payload ?? {})) {
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
      lines.push(`${k}: ${v}`);
    } else if (Array.isArray(v) && v.every((x) => typeof x === "string" || typeof x === "number")) {
      lines.push(`${k}: ${v.join(", ")}`);
    }
  }
  return lines.join("\n").slice(0, maxChars);
}

export interface Hit {
  score: number;
  payload: Record<string, unknown>;
}

// Voyage rerank `data` ([{index, relevance_score}], best first) -> hits in that order,
// scored by relevance. Ignores indexes that do not exist.
export function applyRerank(
  hits: Hit[],
  ranking: Array<{ index: number; relevance_score: number }>,
  limit: number,
): Hit[] {
  const out: Hit[] = [];
  for (const r of ranking) {
    const hit = hits[r.index];
    if (hit) out.push({ score: r.relevance_score, payload: hit.payload });
    if (out.length >= limit) break;
  }
  return out;
}
